import { useContext, useState, useRef, useEffect } from 'react'
import { DataProvider } from '../../store/ContextProvider'

export default function ImportInfo() {
  const { BeURL } = useContext(DataProvider)
  const [messages, setMessages] = useState([])
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(null)
  const fileInputRef = useRef()
  const messagesEndRef = useRef()

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight
    }
  }, [messages])

  const handleImport = async () => {
    const file = fileInputRef.current.files[0]
    if (!file) {
      alert('Please select an Excel file first.')
      return
    }

    setMessages([])
    setUploading(true)

    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await fetch(`${BeURL}/import-candidate-info`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })

      if (!res.ok) {
        throw new Error(`Server responded ${res.status}`)
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // Split by SSE message delimiter
        let parts = buffer.split('\n\n')
        buffer = parts.pop()

        parts.reverse().forEach(part => {
          part.split('\n').forEach(line => {
            if (line.startsWith('data: ')) {
              try {
                const payload = JSON.parse(line.replace(/^data: /, ''))
                setMessages(prev => [...prev, payload.message || payload.error])
                if (payload.progress || payload.progress === 0) {
                  setProgress(payload.progress)
                }
              } catch (e) {
                // ignore non-json or partial
              }
            }
          })
        })
      }
    } catch (err) {
      setMessages(prev => [...prev, `Error: ${err.message}`])
    } finally {
      setMessages(prev => [...prev, '✅ Upload complete!'])
      setProgress(0)
      setUploading(false)
      setProgress(null)
    }
  }

  return (
    <div className="max-w-xl mx-auto p-6 bg-white shadow-md rounded-lg">
      <h2 className="text-2xl font-semibold mb-4">Import Candidate Info</h2>

      <div className="flex items-center space-x-4 mb-6">
        <input
          type="file"
          accept=".xls,.xlsx"
          ref={fileInputRef}
          className="block w-full text-sm text-gray-600
                     file:mr-4 file:py-2 file:px-4
                     file:rounded file:border-0
                     file:text-sm file:font-semibold
                     file:bg-blue-50 file:text-blue-700
                     hover:file:bg-blue-100"
        />
        <button
          onClick={handleImport}
          disabled={uploading}
          className={`
            px-6 py-2 rounded-lg font-medium
            ${uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}
            text-white
          `}
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>

      {
        (uploading && (progress || progress === 0)) &&
        <div className="flex items-center gap-2 my-2">
          <div className="w-full h-2 bg-secondary-200 rounded overflow-hidden">
            <div
              className="h-full bg-primary-400 transition-all duration-300"
              style={{ width: `${uploading ? progress || 0 : 0}%` }}
            ></div>
          </div>
          <span className="text-xs text-gray-500">
            {`${progress}%`}
          </span>
        </div>}


      <div className="h-64 overflow-y-auto bg-gray-50 p-4 rounded-lg border border-gray-200" ref={messagesEndRef}>
        {messages.length === 0 && (
          <p className="text-gray-500">No messages yet.</p>
        )}
        <ul className="space-y-2">
          {messages.map((msg, idx) => (
            <li key={idx} className="text-gray-800">
              {msg}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
